import React, { useEffect, useState } from 'react';
import LocationAutocomplete from './LocationAutocomplete';
import TripTypeTabs from './TripTypeTabs';

const formatDate = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

const isValidDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const CabsSearchWidget = ({ initialValues, onSubmit, compact, onValuesChange }) => {
  const [tripType, setTripType] = useState(initialValues.tripType || 'ONEWAY');
  const [pickupLocationId, setPickupLocationId] = useState(initialValues.pickupLocationId || '');
  const [dropLocationId, setDropLocationId] = useState(initialValues.dropLocationId || '');
  const [pickupDate, setPickupDate] = useState(formatDate(initialValues.pickupDate));
  const [pickupTime, setPickupTime] = useState(initialValues.pickupTime || '');
  const [returnDate, setReturnDate] = useState(formatDate(initialValues.returnDate));
  const [passengers, setPassengers] = useState(initialValues.passengers || 1);

  useEffect(() => {
    setTripType(initialValues.tripType || 'ONEWAY');
    setPickupLocationId(initialValues.pickupLocationId || '');
    setDropLocationId(initialValues.dropLocationId || '');
    setPickupDate(formatDate(initialValues.pickupDate));
    setPickupTime(initialValues.pickupTime || '');
    setReturnDate(formatDate(initialValues.returnDate));
    setPassengers(initialValues.passengers || 1);
  }, [initialValues]);

  useEffect(() => {
    if (!onValuesChange) return;
    onValuesChange({
      tripType,
      pickupLocationId,
      dropLocationId,
      pickupDate,
      pickupTime,
      returnDate: tripType === 'ROUNDTRIP' ? returnDate : '',
      passengers,
    });
  }, [tripType, pickupLocationId, dropLocationId, pickupDate, pickupTime, returnDate, passengers, onValuesChange]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isFormValid) return;
    onSubmit({
      tripType,
      pickupLocationId,
      dropLocationId,
      pickupDate,
      pickupTime,
      returnDate: tripType === 'ROUNDTRIP' ? returnDate : '',
      passengers,
    });
  };

  const passengerCount = Number(passengers || 0);
  const basicValid =
    pickupLocationId &&
    dropLocationId &&
    isValidDate(pickupDate) &&
    pickupTime &&
    passengerCount >= 1 &&
    passengerCount <= 52;
  const returnValid = tripType === 'ROUNDTRIP' ? isValidDate(returnDate) && returnDate >= pickupDate : true;
  const isFormValid = Boolean(basicValid && returnValid);

  return (
    <form onSubmit={handleSubmit} className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${compact ? '' : 'sm:p-6'}`}>
      <TripTypeTabs value={tripType} onChange={(next) => {
        setTripType(next);
        if (next !== 'ROUNDTRIP') setReturnDate('');
      }} />

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <LocationAutocomplete
          label="Pickup"
          placeholder="Select pickup"
          value={pickupLocationId}
          onChange={setPickupLocationId}
        />
        <LocationAutocomplete
          label="Drop"
          placeholder="Select drop"
          value={dropLocationId}
          onChange={setDropLocationId}
        />
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Passengers</label>
          <input
            type="number"
            min="1"
            max="52"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={passengers}
            onChange={(e) => setPassengers(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pickup date</label>
          <input
            type="date"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pickup time</label>
          <input
            type="time"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={pickupTime}
            onChange={(e) => setPickupTime(e.target.value)}
            required
          />
        </div>
        {tripType === 'ROUNDTRIP' && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Return date</label>
            <input
              type="date"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              min={pickupDate}
              required
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">We will keep your trip details in the URL.</p>
        <button
          type="submit"
          disabled={!isFormValid}
          className={`rounded-full px-5 py-2 text-sm font-semibold ${
            isFormValid ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-500 cursor-not-allowed'
          }`}
        >
          Search cabs
        </button>
      </div>
    </form>
  );
};

export default CabsSearchWidget;
